import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Container,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Fade,
} from '@mui/material';
import { colors, fonts, borderRadius, spacing } from '../constants';
import { authService } from '../services/authService';
import PromotionalSection from '../components/auth/PromotionalSection';
import MicrosoftLoginButton from '../components/auth/MicrosoftLoginButton';
import EmailDivider from '../components/auth/EmailDivider';
import logoImage from '../assets/Airports_Authority_of_India_logo.svg.png';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const emailInputRef = useRef(null);

  useEffect(() => {
    if (emailInputRef.current) {
      emailInputRef.current.focus();
    }
  }, []);

  const handleChange = (e) => {
    setEmail(e.target.value);
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);
    setLoading(true);

    if (!email) {
      setError('Please enter your email address');
      setLoading(false);
      return;
    }

    try {
      await authService.forgotPassword(email);
      setSuccess(true);
    } catch (error) {
      if (error.response?.status === 404) {
        setError('Password reset feature is not yet available. Please contact support.');
      } else {
        setError(error.response?.data?.message || 'Failed to send reset email. Please try again.');
      }
    }

    setLoading(false);
  };

  const handleMicrosoftLogin = () => {
    // TODO: Implement Microsoft OAuth
    // console.log('Microsoft login clicked');
  };

  return (
    <Box
      sx={{
        display: 'flex',
        height: '100vh',
        overflow: 'hidden',
        backgroundColor: colors.background,
      }}
    >
      {/* Left Column - Forgot Password Form */}
      <Box
        sx={{
          width: { xs: '100%', md: '60%' },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: { xs: spacing.lg, md: spacing.xl },
          backgroundColor: colors.backgroundWhite,
          height: '100vh',
          overflow: 'auto',
          position: 'relative',
        }}
      >
        <Container
          maxWidth="sm"
          sx={{
            width: '100%',
            py: 0,
            px: { xs: spacing.md, md: spacing.lg },
          }}
        >
          <Fade in={true} timeout={800}>
            <Box
              sx={{
                width: '100%',
                maxWidth: '440px',
                margin: '0 auto',
                animation: 'fadeInUp 0.8s ease-out',
                '@keyframes fadeInUp': {
                  from: {
                    opacity: 0,
                    transform: 'translateY(20px)',
                  },
                  to: {
                    opacity: 1,
                    transform: 'translateY(0)',
                  },
                },
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-start',
                  mb: spacing.lg,
                }}
              >
                <Box
                  component="img"
                  src={logoImage}
                  alt="Airports Authority of India logo"
                  sx={{
                    height: { xs: 44, md: 56 },
                    width: 'auto',
                  }}
                />
              </Box>
              {/* Header */}
              <Box sx={{ textAlign: 'left', mb: spacing.xl }}>
                <Typography
                  variant="h4"
                  component="h1"
                  sx={{
                    fontSize: { xs: '28px', md: '32px' },
                    fontWeight: fonts.weightBold,
                    color: colors.headerColor,
                    mb: spacing.sm,
                    lineHeight: 1.2,
                    letterSpacing: '-0.02em',
                  }}
                >
                  {success ? 'Check Your Email' : 'Forgot Password'}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: fonts.descriptionSize,
                    color: colors.textSecondary,
                    fontWeight: fonts.weightNormal,
                    lineHeight: 1.5,
                  }}
                >
                  {success
                    ? 'Password reset instructions have been sent to your email'
                    : 'Enter your email address and we will send you instructions to reset your password.'}
                </Typography>
              </Box>

              {/* Error Alert */}
              {error && (
                <Fade in={!!error} timeout={300}>
                  <Alert
                    severity="error"
                    icon={false}
                    sx={{
                      borderRadius: borderRadius.md,
                      py: spacing.xs,
                      px: spacing.sm,
                      fontSize: fonts.fontSizeSm,
                      minHeight: 'auto',
                      backgroundColor: 'rgba(239, 68, 68, 0.08)',
                      color: colors.error,
                      border: `1px solid rgba(239, 68, 68, 0.2)`,
                      mb: spacing.md,
                      '& .MuiAlert-message': {
                        padding: 0,
                        fontWeight: fonts.weightMedium,
                      },
                    }}
                  >
                    {error}
                  </Alert>
                </Fade>
              )}

              {/* Success Alert */}
              {success && (
                <Fade in={!!success} timeout={300}>
                  <Alert
                    severity="success"
                    icon={false}
                    sx={{
                      borderRadius: borderRadius.md,
                      py: spacing.xs,
                      px: spacing.sm,
                      fontSize: fonts.fontSizeSm,
                      minHeight: 'auto',
                      backgroundColor: 'rgba(34, 197, 94, 0.08)',
                      color: colors.success,
                      border: `1px solid rgba(34, 197, 94, 0.2)`,
                      mb: spacing.md,
                      '& .MuiAlert-message': {
                        padding: 0,
                        fontWeight: fonts.weightMedium,
                      },
                    }}
                  >
                    Please check your email for further instructions.
                  </Alert>
                </Fade>
              )}

              {!success ? (
                <>
                  {/* Form */}
                  <Box
                    component="form"
                    onSubmit={handleSubmit}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: spacing.md,
                    }}
                  >
                    {/* Email Input */}
                    <TextField
                      inputRef={emailInputRef}
                      fullWidth
                      name="email"
                      type="email"
                      value={email}
                      onChange={handleChange}
                      required
                      disabled={loading}
                      size="small"
                      placeholder="Email address *"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: borderRadius.md,
                          fontSize: '12px',
                          backgroundColor: colors.backgroundWhite,
                          transition: 'all 0.3s ease',
                          cursor: 'text',
                          '& fieldset': {
                            borderColor: colors.borderDefault,
                            borderWidth: '1px',
                            borderStyle: 'solid',
                            borderRadius: borderRadius.md,
                            top: 0,
                          },
                          '&:hover': {
                            cursor: 'text',
                            '& fieldset': {
                              borderColor: colors.borderHover,
                              borderWidth: '1px',
                              borderRadius: borderRadius.md,
                            },
                          },
                          '&.Mui-focused': {
                            boxShadow: 'none',
                            cursor: 'text',
                            '& fieldset': {
                              borderColor: colors.borderFocus,
                              borderWidth: '2px',
                              borderRadius: borderRadius.md,
                            },
                          },
                          '&.Mui-error': {
                            '& fieldset': {
                              borderColor: colors.error,
                              borderRadius: borderRadius.md,
                            },
                          },
                        },
                        '& .MuiInputBase-input': {
                          padding: '10px 12px',
                          color: colors.textPrimary,
                          fontWeight: fonts.weightNormal,
                          fontSize: '12px',
                          lineHeight: 1.4,
                          cursor: 'text',
                          '&:focus': {
                            cursor: 'text',
                          },
                        },
                      }}
                    />

                    {/* Submit Button */}
                    <Button
                      type="submit"
                      fullWidth
                      variant="contained"
                      disabled={loading}
                      sx={{
                        mt: spacing.xs,
                        py: spacing.sm,
                        minHeight: '44px',
                        borderRadius: borderRadius.md,
                      background: colors.primaryGradient,
                        textTransform: 'none',
                        fontSize: fonts.descriptionSize,
                        fontWeight: fonts.weightSemiBold,
                        letterSpacing: fonts.letterSpacingNormal,
                      boxShadow: `0 12px 24px ${colors.primaryShadow}`,
                        transition: 'all 0.3s ease',
                        '&:hover': {
                        background: colors.primaryGradientHover,
                        boxShadow: `0 16px 32px ${colors.primaryShadow}`,
                          transform: 'translateY(-2px)',
                        },
                        '&:active': {
                          transform: 'translateY(0)',
                        },
                        '&:disabled': {
                          backgroundColor: colors.backgroundSoft,
                          color: colors.textTertiary,
                          boxShadow: 'none',
                          opacity: 0.6,
                        },
                      }}
                    >
                      {loading ? (
                        <CircularProgress size={20} sx={{ color: colors.textWhite }} />
                      ) : (
                        'Send Reset Link'
                      )}
                    </Button>
                  </Box>

                  {/* Divider */}
                  <EmailDivider text="Or" />

                  {/* Microsoft Login Button */}
                  <MicrosoftLoginButton onClick={handleMicrosoftLogin} />

                  {/* Back to Login Link */}
                  <Box sx={{ textAlign: 'center', mt: spacing.lg }}>
                    <Typography
                      sx={{
                        fontSize: fonts.fontSizeSm,
                        color: colors.textSecondary,
                        fontWeight: fonts.weightNormal,
                      }}
                    >
                      Remember your password?{' '}
                      <Link
                        to="/login"
                        style={{
                          color: colors.primary,
                          textDecoration: 'none',
                          fontWeight: fonts.weightSemiBold,
                          transition: 'color 0.2s ease',
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.color = colors.primaryHover;
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.color = colors.primary;
                        }}
                      >
                        Back to Login
                      </Link>
                    </Typography>
                  </Box>
                </>
              ) : (
                /* Back to Login Link */
                <Box sx={{ textAlign: 'center', mt: spacing.md }}>
                  <Link
                    to="/login"
                    style={{
                      color: colors.primary,
                      textDecoration: 'none',
                      fontSize: fonts.fontSizeSm,
                      fontWeight: fonts.weightSemiBold,
                      transition: 'color 0.2s ease',
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.color = colors.primaryHover;
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.color = colors.primary;
                    }}
                  >
                    Back to Login
                  </Link>
                </Box>
              )}
            </Box>
          </Fade>
        </Container>
      </Box>

      {/* Right Column - Promotional Section */}
      <PromotionalSection />
    </Box>
  );
};

export default ForgotPassword;
